import { appConfigService } from '@/providers/services/app/app-config';
import { PERMISSION_LIST, DEMO_TENANT_ACCOUNT, USER_TYPE } from '@/utils/constants';
import { authAPI } from '@/providers/apis/auth/auth';
import { sessionService } from '@/providers/services/auth/session';
import { appConfigAPI } from '@/providers/apis/app/app-config';
import router from '@/routers/router';
import { TenantAccountListModel } from '@/models/auth/session';
import StaticAssetsMixin from '@/helpers/mixins/static-assets-mixin';
import { tenantSettingsAPI } from '@/providers/apis/tenant-settings/tenant-settings';

/**
 * Permission class help to show UI based on users permissions
 */
export class Permission {

  private static INSTANCE = new Permission();

  static get instance() {
    return this.INSTANCE;
  }

  public get userRole() {
    return appConfigService.getAppUserRole();
  }

  public get isCapLibrary() {
    const userIds = appConfigService.libraryPageUsers();
    const userId = sessionService.getSession()!.user_id;
    return userIds.indexOf(userId) !== -1;
  }

  public get publisherStatus() {
    const includeCap = appConfigService.includeCapLibrayList() || {};
    const userId = sessionService.getSession()!.user_id;
    return (includeCap && includeCap[userId]) ? includeCap[userId] : 'unpublished';
  }

  public get isDemoTenant() {
    const userId = sessionService.getSession()!.user_id;
    return DEMO_TENANT_ACCOUNT.indexOf(userId) !== -1;
  }

  /**
   * Help to identify roles by pages
   */
  public ACL = PERMISSION_LIST;

  /**
   * Show the menus based on permission
   * @param value
   */
  public hasMenuAccess(value: any) {
    return this.userRole.menus.indexOf(value) !== -1;
  }

  /**
   * Show the pages based on permissions
   * @param component
   * @param value
   */
  public hasPermission(menu: any, value: any) {
    const userRole = this.userRole;
    return userRole.pages[menu]
      ? (userRole.pages[menu].indexOf(value) !== -1 || userRole.pages.all.indexOf('all') !== -1)
      : false;
  }

  public get hasAllPermission() {
    const userRole = this.userRole;
    return userRole && userRole.pages.all.indexOf('all') !== -1;
  }

  public isGuestUser(menu: any, value: any) {
    const userRole = this.userRole;
    return userRole.pages[menu].indexOf(value) !== -1;
  }

  /**
   * It hold the landing page for the tenant user
   */
  public landingPage() {
    return this.userRole.landingPage;
  }

  public getAssets(cdnUrls: any, fileName= 'logo') {
    return new StaticAssetsMixin().getAssets(cdnUrls, fileName);
  }

  public getLoginPageInfo() {
        return appConfigService.getLoginPageInfo();
  }

  public getIsMygooruHost() {
      return new StaticAssetsMixin().isMygooruHost();
  }

  public setFavIcon(favIconUrl: string) {
    (() => {
        const link: any = document.querySelector('link[rel*=\'icon\']') || document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = favIconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
    })();
  }

  /**
   * help to login as a demo user
   * @param userDetails holding user login credentials
   */
  public async doLoginInWithCredential(userDetails: any) {
    const username = userDetails.username;
    const password = userDetails.password;
    const accessToken =  userDetails.accesToken;
    await authAPI
      .logInWithCredential(username, password, accessToken)
      .then(
        (session) => {
          this.updateRole(session);
        },
    );
  }

  public async fetchTenantList(email: string, token: string) {
    return await authAPI.getAccountsLogin(email, token);
  }

  public async doLoginInTenantId(tenant: TenantAccountListModel) {
    return await authAPI.getTenantLoginType(tenant);
  }

  public async getLoginType(nonce: string) {
    return authAPI.tenantLoginType(nonce);
  }

  public updateRole(session: any, isReload: boolean = true, isFacet: boolean = false) {
    if (!sessionService.getDemoSessionCopy()) {
      sessionService.setDemoSessionCopy();
    }
    return appConfigAPI.getAppPermissions(session.permissions).then((userRole: any) => {
      tenantSettingsAPI.fetchActiveTenantSettings(session.access_token).then((tenantSetting: any) => {
        const sessionSave: any = session;
        sessionSave.tenant.settings = tenantSetting;
        if (userRole) {
          appConfigService.setAppUserRole(userRole);
          sessionService.setSession(session);
          if (isReload) {
            if (isFacet) {
              window.location.href = window.location.origin + '/facet-search';
            } else {
              window.location.href = window.location.origin + userRole.landingPage;
            }
          }
          // Remove once the ember js not used on mission control
          if (sessionService.getMcUpdate()) {
            sessionService.deleteMcUpdate();
          }
          return userRole.landingPage;
        }
      });
    });
  }

  public loginBasedOnToken(token: string, queryParam: any = {}) {
    return authAPI
      .signInWithToken(token)
      .then(
        (session: any) => {
          if (session === 401) {
            return session;
          } else {
            return appConfigAPI.getAppPermissions(session.permissions).then((userRole: any) => {
              if (userRole) {
                if (queryParam && queryParam.isHideRoleMenu) {
                  session.isHideRoleMenu = JSON.parse(queryParam.isHideRoleMenu);
                }
                const params = {
                  isCompetency: queryParam.isCompetency ? true : false,
                  isResource: queryParam.isResource ? true : false,
                };
                window.localStorage.setItem('isCollab', JSON.stringify(params));
                appConfigService.setAppUserRole(userRole);
                sessionService.setSession(session);
                if (queryParam && queryParam.redirectUrl) {
                  document.location.href = document.location.origin + queryParam.redirectUrl;
                } else {
                  document.location.href = document.location.origin + userRole.landingPage;
                }
              }
            });
          }
        });
  }

  public getUserType() {
    let userType;
    if (sessionService.getSession()!.permissions.indexOf('ALL_ACCESS') !== -1) {
      userType = USER_TYPE.superUser;
    } else if (sessionService.getSession()!.permissions.indexOf('VIEW_LR_AS_DU') !== -1) {
      userType = USER_TYPE.districtUser;
    } else if (sessionService.getSession()!.permissions.indexOf('VIEW_LR_AS_CP') !== -1) {
      userType = USER_TYPE.contentProvider;
    }
    return userType;
  }

  public getTenantId() {
    const userDetails: any = sessionService.getSession();
    return userDetails.tenant.tenant_id;
  }
}

export default Permission.instance;
